import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { meetingsAPI, teachersAPI } from '../../services/api';
import PageBackButton from '../../components/PageBackButton';
import { useAuth } from '../../contexts/AuthContext';
import StaffMeetingList from './StaffMeetingList';
import { buildMeetingListParams, olderArchiveToYmd, unwrapMeetingsResponse } from './meetingListUtils';

function unwrapList(res) {
  const body = res?.data;
  const rows = body?.data ?? body;
  return Array.isArray(rows) ? rows : [];
}

function teacherLabel(t) {
  const name = [t.first_name, t.last_name].filter(Boolean).join(' ').trim();
  return name || t.email || t.id;
}

const emptyForm = {
  title: '',
  meetingDate: '',
  startTime: '',
  endTime: '',
  joinUrl: '',
  agenda: '',
  notes: '',
  teachers: [],
};

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const MeetingManager = () => {
  const { isAdmin, isTeacherAdmin, isTeachingLead } = useAuth();
  const canManage = isAdmin() || isTeacherAdmin() || isTeachingLead();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [teacherError, setTeacherError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [range, setRange] = useState('recent');
  const [search, setSearch] = useState('');
  const [archiveFrom, setArchiveFrom] = useState('');
  const [archiveTo, setArchiveTo] = useState(olderArchiveToYmd());
  const [page, setPage] = useState(1);
  const searchDebounced = useDebouncedValue(search);

  const listParams = useMemo(
    () => buildMeetingListParams({ range, search: searchDebounced, archiveFrom, archiveTo, page }),
    [range, searchDebounced, archiveFrom, archiveTo, page]
  );

  const meetingsQuery = useQuery(['staff-meetings', listParams], () =>
    meetingsAPI.list(listParams).then((res) => unwrapMeetingsResponse(res))
  );
  const teachersQuery = useQuery(['teachers', 'meetings'], () =>
    teachersAPI.getAll({ limit: 500, status: 'active' }).then((res) => unwrapList(res))
  );

  const meetings = meetingsQuery.data?.rows || [];
  const pagination = meetingsQuery.data?.pagination;

  const teacherOptions = useMemo(
    () => (teachersQuery.data || []).map((t) => ({ ...t, label: teacherLabel(t) })),
    [teachersQuery.data]
  );

  const saveMutation = useMutation(
    (payload) => (editingId ? meetingsAPI.update(editingId, payload) : meetingsAPI.create(payload)),
    {
      onSuccess: () => {
        toast.success(editingId ? 'Meeting updated' : 'Meeting created');
        setForm(emptyForm);
        setEditingId(null);
        setShowForm(false);
        queryClient.invalidateQueries(['staff-meetings']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save meeting'),
    }
  );

  const deleteMutation = useMutation((id) => meetingsAPI.remove(id), {
    onSuccess: () => {
      toast.success('Meeting removed');
      queryClient.invalidateQueries(['staff-meetings']);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not delete meeting'),
  });

  const attendanceMutation = useMutation(
    ({ meetingId, teacherId, attendance }) => meetingsAPI.setAttendance(meetingId, { teacherId, attendance }),
    {
      onSuccess: () => queryClient.invalidateQueries(['staff-meetings']),
      onError: (err) => toast.error(err.response?.data?.message || 'Could not update attendance'),
    }
  );

  if (!canManage) {
    return (
      <Box>
        <PageBackButton />
        <Typography>You do not have access to manage meetings.</Typography>
      </Box>
    );
  }

  const submit = (e) => {
    e.preventDefault();
    if (!form.teachers.length) {
      setTeacherError('Assign at least one teacher');
      toast.error('Assign at least one teacher');
      return;
    }
    setTeacherError('');
    saveMutation.mutate({
      title: form.title,
      meetingDate: form.meetingDate,
      startTime: form.startTime,
      endTime: form.endTime,
      joinUrl: form.joinUrl,
      agenda: form.agenda,
      meetingNotes: form.notes,
      teacherIds: form.teachers.map((t) => t.id),
    });
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    const selected = teacherOptions.filter((t) =>
      (m.participants || []).some((p) => String(p.teacherId) === String(t.id))
    );
    setForm({
      title: m.title || '',
      meetingDate: m.meetingDate || '',
      startTime: m.startTime || '',
      endTime: m.endTime || '',
      joinUrl: m.joinUrl || '',
      agenda: m.agenda || '',
      notes: m.meetingNotes || m.notes || '',
      teachers: selected,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setTeacherError('');
    setShowForm(false);
  };

  return (
    <Box>
      <PageBackButton />
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
        <Box>
          <Typography variant="h5">Staff meetings</Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule a meeting, assign teachers, and mark who attended. Present teachers get this time on their timesheet.
          </Typography>
        </Box>
        {!showForm ? (
          <Button variant="contained" onClick={() => setShowForm(true)}>
            New meeting
          </Button>
        ) : null}
      </Stack>

      <Collapse in={showForm}>
        <Paper component="form" onSubmit={submit} sx={{ p: 2, mb: 3, mt: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {editingId ? 'Edit meeting' : 'New meeting'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.meetingDate}
                onChange={(e) => setForm({ ...form, meetingDate: e.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Start"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="End"
                type="time"
                InputLabelProps={{ shrink: true }}
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Join link"
                value={form.joinUrl}
                onChange={(e) => setForm({ ...form, joinUrl: e.target.value })}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={teacherOptions}
                getOptionLabel={(o) => o.label || teacherLabel(o)}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                value={form.teachers}
                onChange={(_e, value) => {
                  setForm({ ...form, teachers: value });
                  if (value.length) setTeacherError('');
                }}
                loading={teachersQuery.isLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assign teachers"
                    error={Boolean(teacherError)}
                    helperText={teacherError || 'Select one or more teachers'}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Agenda (optional)"
                value={form.agenda}
                onChange={(e) => setForm({ ...form, agenda: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Meeting notes (optional)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                fullWidth
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saveMutation.isLoading}>
              {editingId ? 'Save changes' : 'Create meeting'}
            </Button>
            <Button onClick={resetForm}>Cancel</Button>
          </Stack>
        </Paper>
      </Collapse>

      <StaffMeetingList
        meetings={meetings}
        pagination={pagination}
        loading={meetingsQuery.isLoading}
        range={range}
        onRangeChange={(next) => {
          setRange(next);
          setPage(1);
        }}
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        archiveFrom={archiveFrom}
        archiveTo={archiveTo}
        onArchiveFromChange={(value) => {
          setArchiveFrom(value);
          setPage(1);
        }}
        onArchiveToChange={(value) => {
          setArchiveTo(value);
          setPage(1);
        }}
        onPageChange={setPage}
        variant="manage"
        onEdit={startEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        onAttendance={(payload) => attendanceMutation.mutate(payload)}
      />
    </Box>
  );
};

export default MeetingManager;
